import React from 'react'
import { Modal, Form, Input, Select, InputNumber, DatePicker, Checkbox, Spin, AutoComplete, Tooltip, Typography } from 'antd'
import { CheckSquareOutlined, CloseCircleOutlined } from '@ant-design/icons'
import { useTreatmentForm, useClient } from '../../../hooks'
import { useCaseViewContext } from '../../../context'
import { StyledButton } from '../../Button'
import { MODAL_MODES } from '../../../constants'
import {
    previewComposedNote,
    templateHasSuffix,
    TEMPLATE_VARIABLE_DESCRIPTIONS,
} from '../../../utils/pharmacyNoteUtils'
import dayjs from 'dayjs'
import {
    NoteTemplateKeyWrapper,
    NoteTemplateKeyTitle,
    NoteTemplateKeyTemplate,
    NoteTemplateKeyTable,
    NoteTemplateKeyPlaceholderCell,
    NoteTemplateKeyCode,
    NoteTemplateKeyDescCell,
    NoteTemplateKeyIcon,
    ComposedNotePreview,
    ComposedNoteLabel,
    ComposedNoteBody,
    ComposedNoteFooter,
    ComposedNoteRemaining,
    ComposedNoteTotal,
} from './Treatments.styles'

const { Option } = Select
const { TextArea } = Input

const NoteTemplateKey = ({ noteTemplate }) => {
    const usedPlaceholders = TEMPLATE_VARIABLE_DESCRIPTIONS.filter(
        ({ variable }) => noteTemplate.includes(variable)
    )
    return (
        <NoteTemplateKeyWrapper>
            <NoteTemplateKeyTitle>Note template for this pharmacy:</NoteTemplateKeyTitle>
            <NoteTemplateKeyTemplate>{noteTemplate}</NoteTemplateKeyTemplate>
            {usedPlaceholders.length > 0 && (
                <NoteTemplateKeyTable>
                    <tbody>
                        {usedPlaceholders.map(({ variable, description }) => (
                            <tr key={variable}>
                                <NoteTemplateKeyPlaceholderCell>
                                    <NoteTemplateKeyCode>{variable}</NoteTemplateKeyCode>
                                </NoteTemplateKeyPlaceholderCell>
                                <NoteTemplateKeyDescCell>
                                    {description}
                                </NoteTemplateKeyDescCell>
                            </tr>
                        ))}
                    </tbody>
                </NoteTemplateKeyTable>
            )}
        </NoteTemplateKeyWrapper>
    )
}

export const TreatmentModal = ({ 
    mode, 
    selectedPrescriptionId,
    selectedPrescription,
    onCancel, 
    onOk,
    templates,
    templatesLoading,
    searchTemplates,
    dispenseUnits,
    getDispenseUnitById
}) => {
    const { caseDetails, currentPharmacy, currentPharmacyId, apiClasses, apiType } = useCaseViewContext()

    const { clientName } = useClient(caseDetails?.clientId, apiClasses)

    const {
        form,
        loading,
        medicationSearchState,
        showPharmacyNotes,
        setShowPharmacyNotes,
        pharmacyNoteConfig,
        searchMedications,
        selectMedication,
        selectStrength,
        handleFinish,
        handleCancel,
        selectedTemplate,
        handleSelectTemplate
    } = useTreatmentForm({
        onCancel,
        onOk,
        apiClasses,
        apiType,
        caseDetails,
        currentPharmacy,
        currentPharmacyId,
        selectedPrescriptionId,
        selectedPrescription,
        templates,
        templatesLoading,
        searchTemplates,
        dispenseUnits,
        getDispenseUnitById,
        mode
    })

    if (!mode) return null

    const handleFormFinish = (values) => {
        handleFinish(values)
    }

    const handleFormCancel = () => {
        handleCancel()
    }

    return (
        <Modal
            title={mode === MODAL_MODES.ADD ? 'Add New Treatment' : 'Edit Treatment'}
            open={!!mode}
            onCancel={handleFormCancel}
            footer={null}
            centered
            destroyOnClose={true}
        >
            <div style={{ width: '100%', padding: '10px' }}>
                <Spin spinning={loading}>
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleFormFinish}
                        initialValues={{
                            quantity: 30,
                            refills: 0,
                            daysSupply: 30,
                            startDate: dayjs()
                        }}
                        style={{ display: 'flex', flexDirection: 'column', textTransform: 'capitalize', width: '100%' }}
                    >
                        {mode === MODAL_MODES.ADD && (
                            <div style={{ display: 'flex', flexDirection: 'column', width: '100%', margin: '10px 0' }}>
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-end',
                                    marginBottom: '10px'
                                }}> 
                                    <Typography.Text>
                                        <strong>Template Options</strong>
                                    </Typography.Text>
                                </div>
                                <Select
                                    id="treatment-template-select"
                                    value={selectedTemplate?.title || (templates.length ? 'Templates available' : 'None Selected')}
                                    options={templates?.map(template => ({
                                        label: template.DAW
                                            ? <span><strong style={{ color: 'red' }}>DAW</strong> {template.title}</span>
                                            : template.title,
                                        value: template._id
                                    }))}
                                    onSelect={(value) => {
                                        const template = templates?.find(template => template._id === value)
                                        handleSelectTemplate(template)
                                    }}
                                    style={{ width: '100%' }}
                                    loading={templatesLoading}
                                    placeholder={templatesLoading ? 'Loading templates...' : 'Select a template'}
                                />
                            </div>
                        )}

                        <Form.Item
                            label="Medication Name"
                            name="name"
                            rules={[{ required: true, message: 'Please select a medication' }]}
                        >
                        <AutoComplete
                            id="treatment-medication-name-autocomplete"
                            placeholder={medicationSearchState.loading ? "Searching..." : "Search medication name"}
                            onSearch={searchMedications}
                            onSelect={selectMedication}
                            options={medicationSearchState.matches?.map(med => ({
                                label: med.name,
                                value: med.name
                            }))}
                            loading={medicationSearchState.loading}
                            notFoundContent={medicationSearchState.loading ? "Searching..." : "No medications found"}
                        />
                    </Form.Item>

                    <div style={{ display: 'flex', gap: '16px' }}>
                        <Form.Item
                            label="Strength"
                            name="strength"
                            rules={[{ required: true, message: 'Please select a strength' }]}
                            style={{ flex: 1 }}
                        >
                            <Select
                                id="treatment-strength-select"
                                placeholder="Select strength"
                                onChange={selectStrength}
                                disabled={!medicationSearchState.selectedMedication}
                            >
                                {(() => {
                                    const selectedMed = medicationSearchState.selectedMedication
                                    if (!selectedMed) return null
                                    
                                    const availableStrengths = selectedMed.strengths || [selectedMed]
                                    
                                    return availableStrengths.map((strength, index) => (
                                        <Option key={strength.strength || index} value={strength.strength}>
                                            {strength.strength}
                                        </Option>
                                    ))
                                })()}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            label="Quantity"
                            name="quantity"
                            rules={[{ required: true, message: 'Please enter quantity' }]}
                            style={{ flex: 1 }}
                        >
                            <InputNumber
                                id="treatment-quantity-input"
                                min={1}
                                max={999}
                                style={{ width: '100%' }}
                                placeholder="Quantity"
                            />
                        </Form.Item>
                    </div>

                    <div style={{ display: 'flex', gap: '16px' }}>
                        <Form.Item
                            label="Dispense Unit"
                            name="dispenseUnitId"
                            rules={[{ required: true, message: 'Please select dispense unit' }]}
                            style={{ flex: 1 }}
                        >
                            <Select id="treatment-dispense-unit-select" placeholder="Select unit">
                                {dispenseUnits.map(unit => (
                                    <Option key={unit.StandardDispenseUnitTypeID} value={unit.StandardDispenseUnitTypeID}>
                                        {unit.Name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            label="Refills"
                            name="refills"
                            rules={[{ required: true, message: 'Please enter refills' }]}
                            style={{ flex: 1 }}
                        >
                            <InputNumber
                                id="treatment-refills-input"
                                min={0}
                                style={{ width: '100%' }}
                                placeholder="Refills"
                            />
                        </Form.Item>
                    </div>

                    <div style={{ display: 'flex', gap: '16px' }}>
                        <Form.Item
                            label="Days Supply"
                            name="daysSupply"
                            rules={[{ required: true, message: 'Please enter days supply' }]}
                            style={{ flex: 1 }}
                        >
                            <InputNumber
                                id="treatment-days-supply-input"
                                min={1}
                                max={365}
                                style={{ width: '100%' }}
                                placeholder="Days supply"
                            />
                        </Form.Item>

                        <Form.Item
                            label="Start Date"
                            name="startDate"
                            rules={[{ required: true, message: 'Please select start date' }]}
                            style={{ flex: 1 }}
                        >
                            <DatePicker
                                id="treatment-start-date-picker"
                                style={{ width: '100%' }}
                                format="YYYY-MM-DD"
                                disabledDate={(current) => current && current < dayjs().startOf('day')}
                            />
                        </Form.Item>
                    </div>

                    <Form.Item
                        label="Sig"
                        name="directions"
                        rules={[{ required: true, message: 'Please enter directions' }]}
                    >
                        <TextArea
                            id="treatment-directions-textarea"
                            autoSize={{ minRows: 3 }}
                            maxLength={150}
                            showCount
                            placeholder="Limited to 150 characters..."
                        />
                    </Form.Item>

                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Form.Item 
                            name="providePharmacyNotes" 
                            valuePropName="checked"
                            onChange={(e) => {
                                e.stopPropagation()
                                if (!showPharmacyNotes) {
                                    form.setFieldsValue({ pharmacyNotes: null })
                                }
                                setShowPharmacyNotes(e.target?.checked)
                            }}
                        >
                            <Checkbox id="treatment-notes-checkbox">Notes</Checkbox>
                        </Form.Item>
                        <Form.Item 
                            name="noSubstitutions" 
                            valuePropName="checked"
                            onChange={(e) => e.stopPropagation()}
                        >
                            <Checkbox id="treatment-no-substitutions-checkbox">No Substitutions</Checkbox>
                        </Form.Item>
                        <Form.Item 
                            name="isUrgent" 
                            valuePropName="checked"
                            onChange={(e) => e.stopPropagation()}
                        >
                            <Checkbox id="treatment-urgent-checkbox" style={{ marginLeft: 0 }}>Urgent</Checkbox>
                        </Form.Item>
                        <Form.Item 
                            name="valid" 
                            hidden 
                            valuePropName="checked"
                        >
                            <Checkbox />
                        </Form.Item>
                    </div>
                    
                    <Form.Item
                        hidden={!showPharmacyNotes}
                        shouldUpdate
                        label={
                            <span>
                                Pharmacy Notes
                                {templateHasSuffix(pharmacyNoteConfig?.noteTemplate) && (
                                    <Tooltip
                                        title={<NoteTemplateKey noteTemplate={pharmacyNoteConfig.noteTemplate} />}
                                        overlayStyle={{ maxWidth: 420 }}
                                    >
                                        <NoteTemplateKeyIcon />
                                    </Tooltip>
                                )}
                            </span>
                        }
                    >
                        {({ getFieldValue }) => {
                            const notes      = getFieldValue('pharmacyNotes') || ''
                            const daw        = getFieldValue('noSubstitutions') || false
                            const patientId  = caseDetails?.patient?._id || ''
                            const maxChars   = pharmacyNoteConfig?.maxCharacters ?? 210
                            const template   = pharmacyNoteConfig?.noteTemplate ?? '{{pharmacyNotes}}'
                            const hasSuffix  = templateHasSuffix(template)

                            // Use DAW=true for the overhead so the raw-input cap accounts for
                            // the worst case. This prevents the composed note from exceeding
                            // maxChars if the user checks DAW after filling the field.
                            const overhead    = hasSuffix
                                ? previewComposedNote(template, { pharmacyNotes: '', daw: true, patientId, clientName }).length
                                : 0
                            const effectiveMax = Math.max(1, maxChars - overhead)

                            const composedNote = previewComposedNote(template, { pharmacyNotes: notes, daw, patientId, clientName })
                            const remaining    = maxChars - composedNote.length
                            const remainingColor = remaining < 0 ? '#ff4d4f' : remaining <= 20 ? '#fa8c16' : '#8c8c8c'

                            return (
                                <>
                                    <Form.Item name='pharmacyNotes' noStyle>
                                        <TextArea
                                            id="treatment-pharmacy-notes-textarea"
                                            autoSize={{ minRows: 3 }}
                                            showCount={!hasSuffix}
                                            maxLength={effectiveMax}
                                            placeholder={`Up to ${effectiveMax} characters...`}
                                        />
                                    </Form.Item>

                                    {hasSuffix && (
                                        <>
                                            <ComposedNotePreview $accent={remaining < 0 ? '#ff4d4f' : '#7bad7e'}>
                                                <ComposedNoteLabel type="secondary">
                                                    Composed pharmacy note preview
                                                </ComposedNoteLabel>
                                                <ComposedNoteBody>
                                                    {composedNote || <ComposedNoteLabel type="secondary">Start typing to see preview…</ComposedNoteLabel>}
                                                </ComposedNoteBody>
                                                <ComposedNoteFooter>
                                                    <ComposedNoteRemaining $color={remainingColor} $bold={remaining <= 20}>
                                                        {remaining < 0
                                                            ? `${Math.abs(remaining)} over limit`
                                                            : `${remaining} remaining`}
                                                    </ComposedNoteRemaining>
                                                    <ComposedNoteTotal>
                                                        {composedNote.length} / {maxChars} chars
                                                    </ComposedNoteTotal>
                                                </ComposedNoteFooter>
                                            </ComposedNotePreview>
                                            {remaining < 0 && (
                                                <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>
                                                    Composed note exceeds the {maxChars}-character limit by {Math.abs(remaining)} character(s). Please shorten your notes.
                                                </div>
                                            )}
                                        </>
                                    )}
                                </>
                            )
                        }}
                    </Form.Item>

                    <Form.Item name="ndc" hidden>
                        <Input />
                    </Form.Item>
                    <Form.Item name="dispenseUnitId" hidden>
                        <Input />
                    </Form.Item>
                    <Form.Item name="pharmacy" hidden>
                        <Input />
                    </Form.Item>
                    <Form.Item name="DAW" hidden valuePropName="checked">
                        <Checkbox />
                    </Form.Item>

                    <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', gap: '5px' }}>
                        <StyledButton 
                            id="treatment-cancel-btn"
                            size='medium'
                            htmlType="button"
                            Icon={CloseCircleOutlined}
                            color='#ff4d4f'
                            onClick={handleFormCancel}
                        />
                        <StyledButton 
                            id="treatment-submit-btn"
                            size='medium'
                            htmlType="submit"
                            Icon={CheckSquareOutlined}
                            color='#7BAD7E'
                        />
                    </div>
                </Form>
            </Spin>
            </div>
        </Modal>
    )
}
